import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Dispatch, SetStateAction } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { Modal, DialogContent, Box, CircularProgress } from "@mui/material";
import { useGetCommunityComments } from "../api/hooks/useCommunity";
import type { CommunityGroup, CommunityComment } from "../api/types/community.types";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  selectedGroup: CommunityGroup | null;
}

export default function ViewChatModal({
  open,
  onClose,
  setOpen,
  selectedGroup,
}: ModalProps) {
  const [page, setPage] = useState(1);
  const [allComments, setAllComments] = useState<CommunityComment[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const limit = 10;

  // Fetch comments when modal opens and group is selected
  const { data: commentsData, isLoading: isLoadingComments, error: commentsError } = useGetCommunityComments(
    { 
      communityId: selectedGroup?._id || '',
      page,
      limit,
    },
    { enabled: open && !!selectedGroup?._id }
  );

  // Reset state when modal opens/closes or group changes
  useEffect(() => {
    if (open && selectedGroup?._id) {
      setPage(1);
      setAllComments([]);
      setTotalCount(0);
    }
  }, [open, selectedGroup?._id]);

  // Extract and accumulate comments from response
  useEffect(() => {
    if (commentsData?.data) {
      const data = commentsData.data;
      let newComments: CommunityComment[] = [];
      let newTotalCount = 0;

      // Handle nested structure: data.data.list
      if (data && typeof data === 'object' && 'list' in data && Array.isArray(data.list)) {
        newComments = data.list;
        newTotalCount = data.totalCount || 0;
      } else if (Array.isArray(data)) {
        newComments = data;
        newTotalCount = data.length;
      }

      if (page === 1) {
        // First page - replace all comments
        setAllComments(newComments);
      } else {
        // Subsequent pages - append new comments
        setAllComments((prev) => [...prev, ...newComments]);
      }
      
      setTotalCount(newTotalCount);
      setIsLoadingMore(false);
    }
  }, [commentsData, page]);

  // Calculate if there are more pages to load
  const hasMore = allComments.length < totalCount;
  const totalPages = Math.ceil(totalCount / limit);

  // Handle scroll to detect when near bottom
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const target = e.currentTarget;
    const scrollTop = target.scrollTop;
    const scrollHeight = target.scrollHeight;
    const clientHeight = target.clientHeight;

    // Check if scrolled near bottom (within 100px)
    if (scrollHeight - scrollTop - clientHeight < 100) {
      if (hasMore && !isLoadingMore && !isLoadingComments && page < totalPages) {
        setIsLoadingMore(true);
        setPage((prev) => prev + 1);
      }
    }
  }, [hasMore, isLoadingMore, isLoadingComments, page, totalPages]);

  // Format date helper
  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return "";
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
      return "";
    }
  };

  // Get user display name
  const getUserName = (comment: CommunityComment | any): string => {
    if (typeof comment.userId === 'object' && comment.userId) {
      if (comment.userId.fullName) return comment.userId.fullName;
      if (comment.userId.name) return comment.userId.name;
      if (comment.userId.email) return comment.userId.email;
    }
    return "Unknown User";
  };

  // Get user image
  const getUserImage = (comment: CommunityComment | any): string | undefined => {
    if (typeof comment.userId === 'object' && comment.userId?.image) {
      return comment.userId.image;
    }
    return undefined;
  };

  return (
    <Modal  
      className="modal addNotification_modal"
      open={open}
      onClose={onClose}
    >
      <div className="modal-dialog" style={{ maxWidth: "800px", width: "90%" }}>
        <div className="modal-body">
          {/* Modal Header */}
          <div className="modal_title">
            <h2>Group Chat - {selectedGroup?.name || "Community"}</h2>
            <button
              type="button"
              className="btn-close"
              onClick={() => setOpen(false)}
            >
              <CloseIcon />
            </button>
          </div>

          {/* Chat Content */}
          <DialogContent 
            ref={scrollContainerRef}
            onScroll={handleScroll}
            style={{ 
              maxHeight: "600px", 
              overflowY: "auto", 
              padding: "20px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {isLoadingComments && page === 1 ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress />
              </Box>
            ) : commentsError ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <p style={{ color: "red" }}>
                  {commentsError?.response?.data?.message || "Failed to load comments"}
                </p>
              </Box>
            ) : allComments.length === 0 ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <p>No comments available.</p>
              </Box>
            ) : (
              <>
                <div>
                  {allComments.map((comment: CommunityComment) => (
                    <div key={comment._id} style={{ marginBottom: "20px" }}>
                      {/* Main Comment */}
                      <div
                        style={{
                          padding: "15px",
                          borderRadius: "8px",
                          background: "#f5f5f5",
                          borderLeft: "4px solid #1976d2",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "8px" }}>
                          {getUserImage(comment) && (
                            <img
                              src={getUserImage(comment)}
                              alt={getUserName(comment)}
                              style={{
                                width: "40px",
                                height: "40px",
                                borderRadius: "50%",
                                objectFit: "cover",
                              }}
                            />
                          )}
                          <div style={{ flex: 1, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <b style={{ fontSize: "16px", color: "#1976d2" }}>
                              {getUserName(comment)}
                            </b>
                            {comment.createdAt && (
                              <small style={{ opacity: 0.6, fontSize: "12px" }}>
                                {formatDate(comment.createdAt)}
                              </small>
                            )}
                          </div>
                        </div>
                        <p style={{ margin: "8px 0", fontSize: "14px", lineHeight: "1.5" }}>
                          {comment.comment}
                        </p>
                      </div>

                      {/* Replies */}
                      {comment.replies && comment.replies.length > 0 && (
                        <div style={{ marginLeft: "30px", marginTop: "10px" }}>
                          {comment.replies.map((reply) => (
                            <div
                              key={reply._id}
                              style={{
                                padding: "12px",
                                borderRadius: "8px",
                                background: "#fafafa",
                                borderLeft: "4px solid #9e9e9e",
                                marginBottom: "10px",
                              }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                                <b style={{ fontSize: "14px", color: "#666" }}>
                                  {typeof reply.userId === 'object' && reply.userId
                                    ? (reply.userId.fullName || reply.userId.name || reply.userId.email || "Unknown User")
                                    : "Unknown User"}
                                </b>
                                {reply.createdAt && (
                                  <small style={{ opacity: 0.6, fontSize: "11px" }}>
                                    {formatDate(reply.createdAt)}
                                  </small>
                                )}
                              </div>
                              <p style={{ margin: "6px 0", fontSize: "13px", lineHeight: "1.4", color: "#555" }}>
                                {reply.comment}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Loading more indicator */}
                {(isLoadingMore || (isLoadingComments && page > 1)) && (
                  <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                    <CircularProgress size={24} />
                  </Box>
                )}

                {/* End of messages indicator */}
                {!hasMore && allComments.length > 0 && (
                  <Box display="flex" justifyContent="center" alignItems="center" py={2}>
                    <p style={{ opacity: 0.6, fontSize: "12px" }}>No more comments to load</p>
                  </Box>
                )}
              </>
            )}
          </DialogContent>
        </div>
      </div>
    </Modal>
  );
}
